<template>
  <div
    :class="[
      wrapperClass,
      {
        relative: labelPin,
        'field-error': isError,
        'field-disabled': isDisabled
      }
    ]"
  >
    <label
      v-if="label"
      :for="id"
      class="overflow-hidden text-inherit transition-transform duration-[400ms] ease-quart-out"
      :class="[
        labelPin ? 'absolute left-0 top-4 origin-top-left' : 'label-float',
        labelPin && isFieldFocused
          ? '-translate-y-5 scale-[0.68] lg:scale-[0.43]'
          : 'translate-x-0 scale-100',
        isLabelVisible ? 'inline-block' : 'sr-only',
        {
          'cursor-pointer': id,
          'pointer-events-none text-disabled': isDisabled
        },
        labelClass
      ]"
      ><span
        class="inline-block transition-transform duration-700 ease-quart-out"
        :class="isMounted ? 'translate-y-0' : 'translate-y-full'"
        >{{ label }}<span v-if="isRequired" class="pl-px">*</span></span
      ></label
    >
    <div class="relative">
      <slot />
    </div>
    <div class="form-message overflow-hidden" :class="{ 'form-message-fixed': messageFixed }">
      <Transition name="label">
        <div v-if="isMessageVisible">
          <span
            :class="{
              'bg-gradient-to-r from-pink via-purple to-cyan bg-clip-text text-transparent':
                isError,
              'text-disabled': isDisabled
            }"
            >{{ message }}</span
          >
        </div>
      </Transition>
      <div v-if="slots.bottom">
        <slot name="bottom" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTimeoutFn } from '@vueuse/core'
import { onMounted, useSlots } from 'vue'

export interface FormFieldProps {
  /** Overrides the default wrapper classes */
  wrapperClass?: string
  id: string
  label?: string | boolean
  /** Add classes to label tag */
  labelClass?: string
  labelPin?: boolean
  isFieldFocused?: boolean
  isLabelVisible?: boolean
  isRequired?: boolean
  message?: string | boolean
  messageFixed?: boolean
  isMessageVisible?: boolean
  isDisabled?: boolean
  isError?: boolean
}

withDefaults(defineProps<FormFieldProps>(), {
  wrapperClass: 'form-field',
  labelClass: 'form-label',
  label: 'Label',
  labelPin: false,
  isFieldFocused: false,
  isLabelVisible: true,
  isRequired: false,
  messageFixed: true,
  message: false,
  isMessageVisible: false,
  isDisabled: false,
  isError: false
})

const slots = useSlots()
const isMounted = ref(false)

onMounted(() => {
  useTimeoutFn(() => {
    isMounted.value = true
  }, 100)
})
</script>

<style lang="css">
.label-enter-active,
.label-leave-active {
  transition: transform 600ms cubic-bezier(0.165, 0.84, 0.44, 1);
}

.label-enter-from,
.label-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
